import {DISPLAY_GITHUB_REFERENCES_TOOL_NAME, parseGithubReferencesToolInput} from './github-references'
import type {ClientTool, ClientToolCall} from '@github-ui/ahp-relay/relay-connection-runtime'
import type {ToolCallResult, ToolResultContentType} from '@microsoft/agent-host-protocol'

/**
 * Client tool the agent calls to surface a list of references (PRs, issues,
 * branches, users, external resources, …) as panel-style cards instead of a markdown list. The
 * structured input carries everything the UI needs, so the timeline renders the
 * cards directly from the call's `toolInput` (see `GitHubReferenceCards` + the
 * `AhpTurnView` special-case); `fulfil` only reports back that they were
 * rendered so the model does not repeat them as text.
 *
 * Registered at session-create time via `createSession.activeClient.tools`, so
 * it lands in the agent's create-time tool set (which the daemon freezes
 * thereafter) and is callable on the very first turn.
 *
 * The description is deliberately MUST-shaped. In copilot-experiences#12662 the
 * model read the previous advisory wording as optional and wrote a markdown list
 * instead, because that fallback is always available to it and costs nothing.
 */
export const displayGithubReferencesTool: ClientTool = {
  definition: {
    name: DISPLAY_GITHUB_REFERENCES_TOOL_NAME,
    title: 'Display GitHub references',
    description:
      'Renders pull requests, issues, branches, users, external resources, commits, repositories, discussions, or releases as cards. ' +
      'You MUST call this tool, rather than writing a markdown list, table, or link, whenever your response ' +
      'presents such items as a list or result set, even a list of one, and whenever you create or update ' +
      'one of these items, such as after opening an issue or pull request by any means. Do not also restate ' +
      'the items as text. Plain inline links are only for items mentioned in passing.',
    inputSchema: {
      type: 'object',
      properties: {
        items: {
          type: 'array',
          description: 'The GitHub references to render, in display order.',
          items: {
            type: 'object',
            properties: {
              type: {
                type: 'string',
                enum: ['pr', 'issue', 'branch', 'user', 'external', 'commit', 'repo', 'discussion', 'release'],
                description: 'The kind of reference.',
              },
              title: {type: 'string', description: 'Human-readable title, when required by the reference type.'},
              url: {
                type: 'string',
                description: 'Full absolute http(s) URL. GitHub reference types must use a GitHub host.',
              },
              number: {type: 'integer', description: 'Issue or pull request number, when applicable.'},
              state: {
                type: 'string',
                enum: ['open', 'closed', 'merged', 'draft'],
                description: 'Current state, when applicable.',
              },
              repo: {type: 'string', description: 'Owner/name of the repository, e.g. "github/github-ui".'},
              baseBranch: {type: 'string', description: 'Base branch name for a branch comparison.'},
              headBranch: {type: 'string', description: 'Head branch name for a branch comparison.'},
              metadata: {type: 'string', description: 'Secondary metadata shown below the title.'},
            },
            required: ['url'],
          },
        },
      },
      required: ['items'],
    },
    annotations: {
      readOnlyHint: true,
      destructiveHint: false,
      // Not idempotent: each call renders another card group in the transcript.
      idempotentHint: false,
      openWorldHint: false,
    },
  },
  fulfil: fulfilDisplayGithubReferences,
}

/**
 * What the model supplied, classified before {@link parseGithubReferencesToolInput}
 * flattens every failure to `[]`. Distinguishes an absent or empty list (nothing
 * to render, not an error) from input that could not be read.
 */
type SuppliedInput = {kind: 'absent'} | {kind: 'malformed'} | {kind: 'items'; count: number}

function inspectSuppliedInput(toolInput: unknown): SuppliedInput {
  if (toolInput === undefined || toolInput === null) return {kind: 'absent'}
  let parsed: unknown = toolInput
  if (typeof toolInput === 'string') {
    try {
      parsed = JSON.parse(toolInput)
    } catch {
      return {kind: 'malformed'}
    }
  }
  if (!parsed || typeof parsed !== 'object') return {kind: 'malformed'}
  const items = (parsed as {items?: unknown}).items
  if (!Array.isArray(items)) return {kind: 'malformed'}
  return {kind: 'items', count: items.length}
}

function failedRender(message: string): ToolCallResult {
  return {
    success: false,
    pastTenseMessage: 'Could not display the GitHub references',
    error: {message},
    content: [{type: 'text' as ToolResultContentType.Text, text: message}],
  }
}

/**
 * Report the render back to the model. Rendering happens in the timeline from
 * the call's `toolInput`, so the result carries no data — only a note that the
 * cards are already on screen, which stops the model re-listing the same
 * references as markdown in a follow-up turn (an empty result reads to the host
 * as "no output", prompting it to do so).
 *
 * `normalizeReference` drops any row lacking the fields required by its type or
 * a safe URL, and the timeline renders nothing when no row survives. The
 * description forbids restating the items as text, so reporting success there
 * would leave the user with a blank answer.
 */
function fulfilDisplayGithubReferences(call: ClientToolCall): ToolCallResult {
  const rendered = parseGithubReferencesToolInput(call.toolInput).length

  if (rendered === 0) {
    const supplied = inspectSuppliedInput(call.toolInput)
    if (supplied.kind === 'malformed') {
      return failedRender(
        'The tool input could not be read as an object with an `items` array, so nothing was shown to the ' +
          'user. Retry this tool call with input shaped as ' +
          '{"items": [{"title": "...", "url": "https://github.com/..."}]}.',
      )
    }
    if (supplied.kind === 'items' && supplied.count > 0) {
      return failedRender(
        `None of the ${supplied.count} supplied references could be rendered, so nothing was shown to the ` +
          'user. Every item needs the required fields for its type and a safe absolute URL. Retry ' +
          'this tool call with corrected items, including a title where the item type requires one and an http or https URL.',
      )
    }
  }

  const noun = rendered === 1 ? 'reference' : 'references'
  return {
    success: true,
    pastTenseMessage: rendered > 0 ? `Displayed ${rendered} GitHub ${noun}` : 'Displayed the GitHub references',
    content: [
      {
        type: 'text' as ToolResultContentType.Text,
        text: 'The GitHub references have been rendered as cards in the UI. Do not list them again as text.',
      },
    ],
  }
}
